import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNotifications } from '../context/NotificationContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function VerifyEmailChange() {
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  useEffect(() => {
    const confirmChange = async () => {
      const queryParams = new URLSearchParams(location.search);
      const token = queryParams.get('token');

      if (!token) {
        showError('Invalid link.', 'Please request a new email change link.', 5000);
        setVerifying(false);
        return;
      }

      try {
        const functions = getFunctions();
        const confirmEmailChangeFunction = httpsCallable(functions, 'confirmEmailChange');
        const result = await confirmEmailChangeFunction({ token });

        if (result.data.success) {
          setNewEmail(result.data.email);
          setSuccess(true);
          setVerifying(false);
          showSuccess('Email address updated successfully!');
        } else {
          throw new Error(result.data.message || 'Email change confirmation failed');
        }
      } catch (error) {
        console.error('Error confirming email change:', error);
        showError('Confirmation failed.', 'The link may be invalid or expired. Please request a new one.', 5000);
        setVerifying(false);
      }
    };

    confirmChange();
  }, [location]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex flex-col">
      <Header />

      <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">
              Confirm Email Change
            </h2>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            {verifying ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
                <p className="text-gray-700 font-medium">Confirming your new email address...</p>
              </div>
            ) : success ? (
              <div className="text-center py-6 space-y-4">
                <div className="flex justify-center">
                  <div className="rounded-full bg-green-100 p-3">
                    <CheckCircle className="h-12 w-12 text-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Email updated successfully!</h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Your email address has been changed to <span className="font-medium text-primary">{newEmail}</span>.
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    You can now log in with your new email address.
                  </p>
                </div>
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-lg shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-150"
                  >
                    Go to Login
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 space-y-4">
                <div className="flex justify-center">
                  <div className="rounded-full bg-red-100 p-3">
                    <AlertCircle className="h-12 w-12 text-red-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Email change failed</h3>
                  <p className="mt-2 text-sm text-gray-600">
                    The verification link may be invalid or expired. Please request a new email change from your account settings.
                  </p>
                </div>
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-lg shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-150"
                  >
                    Go to Login
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
